import { useLayoutEffect, useState } from 'react';

/**
 * Keeps an open dropdown list on screen. Measures the room below and above the field — inside
 * the window AND inside every ancestor that clips (overflow auto/hidden: a modal body, a
 * scrolling page) — and opens the list upward when there is not enough room below, capping
 * its height to the room it actually has. Without this a list near the bottom ran off the
 * window and its last entries could not be reached.
 *
 * Returns an inline style for the list ({} while closed). `gap` is the space between field
 * and list; `preferred` the list's normal max height.
 *
 * `fixed: true` is for a list rendered in a PORTAL (document.body): it gets viewport coordinates
 * (position: fixed) and only the WINDOW limits it, so no table, card or scrolling panel can clip
 * it. Every combo inside a table or an `overflow` box needs this — an absolute list there is cut
 * off by the box (the Jira field map, 2026-10-16).
 */
export default function useDropdownFit(anchorRef, open, { preferred = 320, gap = 6, minHeight = 120, fixed = false, minWidth = 220 } = {}) {
  const [style, setStyle] = useState({});

  useLayoutEffect(() => {
    if (!open) { setStyle({}); return undefined; }
    const place = () => {
      const el = anchorRef.current;
      if (!el) return;
      let top = 0;
      let bottom = window.innerHeight;
      for (let p = el.parentElement; !fixed && p && p !== document.body; p = p.parentElement) {
        const s = window.getComputedStyle(p);
        if (/(auto|scroll|hidden|clip)/.test(s.overflowY)) {
          const r = p.getBoundingClientRect();
          top = Math.max(top, r.top);
          bottom = Math.min(bottom, r.bottom);
        }
      }
      const r = el.getBoundingClientRect();
      const margin = 8;
      const below = bottom - r.bottom - gap - margin;
      const above = r.top - top - gap - margin;
      const up = below < Math.min(preferred, 220) && above > below;
      const maxHeight = Math.max(minHeight, Math.min(preferred, up ? above : below));
      if (fixed) {
        // As wide as the field (at least minWidth), never past the window's right edge.
        const width = Math.min(Math.max(r.width, minWidth), window.innerWidth - 2 * margin);
        const left = Math.max(margin, Math.min(r.left, window.innerWidth - width - margin));
        setStyle({
          position: 'fixed', left, width, right: 'auto', margin: 0, maxHeight, zIndex: 4000,
          ...(up ? { top: 'auto', bottom: window.innerHeight - r.top + gap } : { top: r.bottom + gap, bottom: 'auto' }),
        });
        return;
      }
      setStyle(up
        ? { top: 'auto', bottom: '100%', marginTop: 0, marginBottom: gap, maxHeight }
        : { maxHeight });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, open, preferred, gap, minHeight, fixed, minWidth]);

  return style;
}
